<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <!--
        WHAT THE CONSOLE IS LOOKING AT.

        The control that stands where the reception portal's property switcher does, and
        it is deliberately not one: the administration never works in a single house, so
        the question is how much of the group — everything, one region or brand, one
        company, or a handful of houses somebody picked.

        Nothing reloads when it changes. The button says what is chosen and the panel
        says how many houses that is, because „Jadranska regija“ alone does not say
        whether somebody is looking at four hotels or at one.
    -->
    <UPopover v-model:open="open" :content="{ align: 'start' }">
        <UButton
            data-testid="scope-selector"
            :label="label"
            :title="`${$t('scope.label')}: ${label}`"
            :aria-label="`${$t('scope.label')}: ${label}`"
            :ui="{ label: 'truncate' }"
            color="neutral"
            variant="subtle"
            icon="i-lucide-layers"
            trailing-icon="i-lucide-chevron-down"
            size="lg"
            class="max-w-[9rem] sm:max-w-[14rem]"
        />

        <template #content>
            <div class="flex w-80 max-w-[92vw] flex-col gap-3 p-3" data-testid="scope-panel">
                <p class="text-xs font-medium text-muted">{{ $t('scope.label') }}</p>

                <URadioGroup
                    v-model="kind"
                    data-testid="scope-kind"
                    :items="kindItems"
                    value-key="value"
                />

                <!-- Only the picker the chosen kind needs is drawn: a panel showing
                     three empty selects asks somebody to read the radio buttons twice. -->
                <USelectMenu
                    v-if="kind === ScopeKind.Unit"
                    v-model="unitId"
                    data-testid="scope-unit"
                    :items="unitItems"
                    :placeholder="$t('scope.pick_unit')"
                    value-key="value"
                    class="w-full"
                />

                <USelectMenu
                    v-else-if="kind === ScopeKind.Entity"
                    v-model="entityId"
                    data-testid="scope-entity"
                    :items="entityItems"
                    :placeholder="$t('scope.pick_entity')"
                    value-key="value"
                    class="w-full"
                />

                <AppSelectMenu
                    v-else-if="kind === ScopeKind.Properties"
                    v-model="pickedProperties"
                    data-testid="scope-properties"
                    :items="propertyItems"
                    :placeholder="$t('scope.pick_properties')"
                    value-key="value"
                    multiple
                    class="w-full"
                />

                <p class="text-xs text-dimmed">
                    {{ $t('scope.covers', { count: scopeStore.propertyIds.length }) }}
                </p>
            </div>
        </template>
    </UPopover>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AppSelectMenu from '@/components/app/AppSelectMenu.vue'

    import { ScopeKind } from '@/enums/group'
    import { useScopeParams } from '@/libs/scopeParams'
    import { useScopeStore } from '@/stores/scope'

    const { t }      = useI18n()
    const scopeStore = useScopeStore()

    const { scopeLabel } = useScopeParams()

    const open = ref(false)

    /**
     * The kind the PANEL is on, which is not always the kind the selection is.
     *
     * Moving the radio to „regija ali znamka“ before one has been picked must not
     * answer every screen with an empty set — that reads as a group with no hotels
     * rather than as a question not yet finished. So the store stays where it was and
     * the first pick moves it; only „cela skupina“ takes effect at once, because it
     * needs nothing picked.
     */
    const pendingKind = ref<ScopeKind>(scopeStore.kind)

    // The selection can move without this panel: an address bar carrying `?scope=`, and
    // a reconciliation that dropped a unit somebody else deleted. The radio follows it,
    // or it would say „regija“ over a console that is showing the whole group.
    watch(() => scopeStore.kind, (value) => { pendingKind.value = value })

    const kind = computed({
        get: (): ScopeKind => pendingKind.value,
        set: (value: ScopeKind) => {
            pendingKind.value = value

            if (value === ScopeKind.Group) scopeStore.selectGroup()
        },
    })

    /**
     * The four things a selection can be. Every value is a non-empty string — Reka UI
     * throws out of a select item whose value is `''` and takes the rest of the subtree
     * with it (CLAUDE.md, invariant 6), which is why „cela skupina“ is `group` and not
     * the empty string it conceptually is.
     */
    const kindItems = computed(() => [
        { label: t('scope.kind_group'),      value: ScopeKind.Group },
        { label: t('scope.kind_unit'),       value: ScopeKind.Unit },
        { label: t('scope.kind_entity'),     value: ScopeKind.Entity },
        { label: t('scope.kind_properties'), value: ScopeKind.Properties },
    ])

    const unitId = computed({
        get: (): string => scopeStore.unitId,
        set: (value: string) => scopeStore.selectUnit(value),
    })

    const entityId = computed({
        get: (): string => scopeStore.entityId,
        set: (value: string) => scopeStore.selectEntity(value),
    })

    const pickedProperties = computed({
        get: (): string[] => scopeStore.kind === ScopeKind.Properties ? scopeStore.propertyIds : [],
        set: (value: string[]) => scopeStore.selectProperties(value),
    })

    /** The tree, flattened: a region or brand, with a child indented under its parent. */
    const unitItems = computed(() => scopeStore.branches.map(unit => ({
        label: scopeStore.branches.some(parent => parent.id === unit.parent_id)
            ? `— ${unit.name}`
            : unit.name,
        value: unit.id,
    })))

    const entityItems = computed(() => scopeStore.entities
        .map(entity => ({ label: entity.name, value: entity.id })))

    const propertyItems = computed(() => scopeStore.properties
        .map(house => ({ label: house.name, value: house.id })))

    /**
     * What the button says. The name of the thing chosen, never the word for its kind —
     * and it lives in `libs/scopeParams.ts`, because Milo's chip and the report dialog
     * have to say exactly the same thing about exactly the same selection.
     */
    const label = scopeLabel
</script>
